import { usePathname } from "@-/com.npmjs.expo-router";
import { Platform } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { clearOnboarding, startOnboarding } from "@-/dev.improve.haniverse.lib.onboarding";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useMapPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.map.navigator";
import { enterHaniverseFromWelcome } from "@-/dev.improve.haniverse.routes.nav.app.auth.welcome-galaxy-entry";
import { useCallback, useRef } from "react";
import { isOnboardingSkippedByLaunchArgument } from "#is-onboarding-skipped-by-launch-argument";

export type LeaveAuthSheetOptions = {
  /**
   * The account was just made (Sign Up, or "Get Started"), rather than signed in to: a new learner,
   * whose app opens on the onboarding (`lib/onboarding`).
   */
  isNewAccount: boolean;
};

/**
 * How an auth sheet leaves once the account step is done (a session exists, or "Get Started"
 * recorded an address locally). Either way the device now has an account email, which is what the
 * `(app)` layout requires.
 *
 * Presented over the app, the sheet just closes and the user stays where they were. Presented
 * over the welcome gate, which sits outside the `(app)` tab group, it also replaces into Learn.
 * Signing in lands on Learn's map, the app's home, as a launch does: from the welcome galaxy itself
 * (`/auth`) the galaxy dives into its core and the map comes out of it
 * (`enterHaniverseFromWelcome(navigate, "map")`). Entering the app this way starts a fresh Learn
 * session, so the map is the home (greeting the learner) even after a sign-out in this app run.
 *
 * A new account on the native app starts the onboarding instead: the galaxy dives into its core,
 * out of which the onboarding's globe comes, covering Learn's already-open map. Every account
 * enters that map directly, including web and E2E launches that skip onboarding. Signing
 * in leaves no onboarding behind (one a previous account on this device left unfinished, say), and
 * neither does an E2E launch that skips it (`isOnboardingSkippedByLaunchArgument`).
 *
 * Leaves at most once per mounted sheet: the provider buttons and a revealed email form both watch
 * for the new session, and each would otherwise close and navigate again.
 *
 * Only the sheet CONTENT calls this, and it is mounted only while a sheet is open, so the Learn
 * navigator's query-option churn never re-renders the provider that wraps the whole app.
 */
export function useLeaveAuthSheet(close: () => void): (options: LeaveAuthSheetOptions) => void {
  const pathname = usePathname();
  const { mutate: replaceLearnMap } = useMapPageNavigator().replaceImmediately;
  const [setHasLearnFlowStartedThisSession] = useContextSelectors(
    NavContext,
    (c) => c.setHasLearnFlowStartedThisSession,
  );
  const hasLeftRef = useRef(false);
  return useCallback(
    ({ isNewAccount }: LeaveAuthSheetOptions) => {
      if (hasLeftRef.current) {
        return;
      }
      hasLeftRef.current = true;
      close();
      const isWelcomeGate = pathname === "/auth" || pathname.startsWith("/auth/");
      if (!isWelcomeGate) {
        return;
      }
      const isOnboardingSkipped = isOnboardingSkippedByLaunchArgument();
      const isOnboarding = isNewAccount && Platform.OS !== "web" && !isOnboardingSkipped;
      if (isOnboarding) {
        startOnboarding();
      } else if (!isNewAccount || isOnboardingSkipped) {
        clearOnboarding();
      }
      setHasLearnFlowStartedThisSession(false);
      const enterLearn = () => replaceLearnMap(undefined);
      if (pathname === "/auth") {
        enterHaniverseFromWelcome(enterLearn, isOnboarding ? "globe" : "map");
      } else {
        enterLearn();
      }
    },
    [close, pathname, replaceLearnMap, setHasLearnFlowStartedThisSession],
  );
}
